import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import { flowRight } from 'lodash'
import { useTranslation } from 'react-i18next'
import { withRouter } from 'react-router-dom'
import { withErrorBoundary } from 'lib/errorBoundary'
import { isVehicleOld, mapVehiclesForSelect } from 'lib/utils/store/vehicle'
import * as Yup from 'yup'
import * as Nope from 'lib/utils/validation/Nope'
import { getLocale } from 'lib/utils/getLocale'

import { Field, Form } from 'formik'
import ExtendedFormik from 'components/Forms/ExtendedFormik'
import Button from 'components/UI/Button'
import RadioGroup from 'components/Forms/FormElements/RadioGroup'

import './vehicle-selection.pcss'

export const VEHICLE_SELECT_ACTIONS = Object.freeze({
    GOTO: 'goto',
})

const VehicleSelection = ({ action, location, history, vehicles }) => {
    const { t, i18n } = useTranslation(['vehicles', 'formElements'])
    const query = queryString.parse(location.search)

    const gotoTarget = query.target
    const allowAutoselect = query['allow-autoselect'] !== undefined

    const gotoVehicle = useCallback((vehicleId, replace = false) => {
        const targetUrl = decodeURIComponent(gotoTarget)
            .replace(/:vehicleId/g, vehicleId)

        if (replace) {
            history.replace(targetUrl)
        } else {
            history.push(targetUrl)
        }
    }, [history, gotoTarget])

    const handleContinue = (values) => {
        switch (action) {
        case VEHICLE_SELECT_ACTIONS.GOTO:
            gotoVehicle(values.vehicle)
            break
        default:
            throw new Error(`Invalid action "${action}"`)
        }
    }

    const handleAddNewVehicle = () => {
        history.replace(`${getLocale(i18n.language)}/lisaa-ajoneuvo?target=${encodeURIComponent(gotoTarget)}`)
    }

    useEffect(() => {
        if (!vehicles || !vehicles.length) {
            history.replace(`${getLocale(i18n.language)}/lisaa-ajoneuvo?target=${encodeURIComponent(gotoTarget)}`)
        }

        if (allowAutoselect && vehicles && vehicles.length === 1) {
            gotoVehicle(vehicles[0].id, true)
        }
    }, [vehicles, query, history, allowAutoselect, gotoVehicle, gotoTarget, i18n.language])

    return (
        <div className="vehicle-selection">
            <div className="vehicle-selection__select-vehicle">
                <ExtendedFormik
                    id="vehicle-select-form"
                    initialValues={{
                        vehicle: '',
                    }}
                    validationSchema={Nope.schema({
                        vehicle: Yup.string()
                            .required('formElements:validation.required'),
                    })}
                    onSubmit={async (values, actions) => {
                        if (values.vehicle === 'addNewVehicle') {
                            handleAddNewVehicle()
                            return
                        }

                        actions.setSubmitting(true)
                        handleContinue(values)
                    }}
                >
                    {({ isSubmitting, isValidating }) => (
                        <Form noValidate>
                            <Field name="vehicle">
                                {props => (
                                    <RadioGroup
                                        {...props}
                                        type="radio"
                                        options={[
                                            ...mapVehiclesForSelect(t, vehicles.filter(vehicle => !isVehicleOld(vehicle))),
                                            { value: 'addNewVehicle', name: t('vehicles:vehicleSelection.addNewVehicle') },
                                        ]}
                                        required
                                    />
                                )}
                            </Field>
                            <div className="vehicle-selection__actions">
                                <Button
                                    type="submit"
                                    className="button button--centered"
                                    trackName="continue"
                                    disabled={isSubmitting || isValidating}
                                >
                                    {t('common:continue')}
                                </Button>
                            </div>
                        </Form>
                    )}
                </ExtendedFormik>
            </div>
        </div>
    )
}

VehicleSelection.propTypes = {
    action: PropTypes.string,
    vehicles: PropTypes.array,
}

export default flowRight(
    withErrorBoundary,
    withRouter,
)(VehicleSelection)
